import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Briefcase, 
  Shield, 
  Users, 
  CheckCircle2,
  Send
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const positions = [
  "Fiber Splicer",
  "Fiber Technician",
  "Lineman",
  "Construction Crew Member",
  "Equipment Operator",
  "Project Manager",
  "Other"
];

const benefits = [
  "Competitive compensation",
  "Safety-first work environment",
  "Growth opportunities",
  "Work with industry-leading carriers",
  "Professional development",
  "Team-focused culture"
];

const openPositions = [
  {
    title: "Fiber Splicer",
    location: "Ohio",
    type: "Full-time",
    description: "Experienced fiber splicers for fusion splicing and testing on carrier networks."
  },
  {
    title: "Construction Crew Member",
    location: "Ohio",
    type: "Full-time",
    description: "Skilled crew members for OSP fiber and HFC network construction projects."
  },
  {
    title: "Lineman",
    location: "Ohio",
    type: "Full-time",
    description: "Experienced linemen for aerial construction and strand installation."
  }
];

export default function Careers() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, position: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/careers/apply`, formData);
      setSubmitted(true);
      toast.success("Your application has been submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        position: "",
        experience: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="careers-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Careers
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Build your career with C&F Communications. We're looking for skilled 
              professionals who share our commitment to safety and quality.
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                Why Work With Us
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
                Safety-First Culture
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                At C&F Communications, we believe in putting safety first and supporting 
                our team members' growth. Join a company where your skills are valued 
                and your development matters.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-[#00B4D8] flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8] flex gap-6">
                <Shield className="h-10 w-10 text-[#00B4D8] flex-shrink-0" />
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Safety First</h3>
                  <p className="text-slate-600 text-sm">Your safety is our top priority on every job site.</p>
                </div>
              </div>
              <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8] flex gap-6">
                <Users className="h-10 w-10 text-[#00B4D8] flex-shrink-0" />
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Team Environment</h3>
                  <p className="text-slate-600 text-sm">Work alongside experienced professionals who support each other.</p>
                </div>
              </div>
              <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8] flex gap-6">
                <Briefcase className="h-10 w-10 text-[#00B4D8] flex-shrink-0" />
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Career Growth</h3>
                  <p className="text-slate-600 text-sm">Opportunities to advance and develop new skills.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 md:py-32 bg-slate-50 grid-pattern" data-testid="open-positions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Opportunities
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Open Positions
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {openPositions.map((position, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 p-8 hover:border-[#00B4D8] transition-colors"
                data-testid={`position-${index}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-[#00B4D8]/10 text-[#00B4D8] text-xs font-bold uppercase">
                    {position.type}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  {position.title}
                </h3>
                <p className="text-slate-500 text-sm mb-4">{position.location}</p>
                <p className="text-slate-600 text-sm mb-6">{position.description}</p>
                <a 
                  href="#apply"
                  className="text-[#00B4D8] text-sm font-semibold hover:underline inline-flex items-center"
                >
                  Apply Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 md:py-32 bg-white scroll-mt-24" data-testid="application-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Apply Now
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Submit Your Application
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          {submitted ? (
            <div className="text-center py-12 bg-slate-50" data-testid="application-success">
              <CheckCircle2 className="h-16 w-16 text-[#00B4D8] mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Application Received!
              </h3>
              <p className="text-slate-600 mb-6">
                Thank you for your interest in joining C&F Communications. Our team will review your application and contact you if there's a fit.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="text-[#00B4D8] font-semibold hover:underline"
                data-testid="submit-another-button"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="career-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                    placeholder="Your full name"
                    data-testid="career-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                    placeholder="you@email.com"
                    data-testid="career-input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                    placeholder="(555) 123-4567"
                    data-testid="career-input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="position" className="text-slate-700 font-medium">
                    Position Interest *
                  </Label>
                  <Select onValueChange={handleSelectChange} value={formData.position} required>
                    <SelectTrigger 
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      data-testid="career-select-position"
                    >
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {positions.map((pos) => (
                        <SelectItem key={pos} value={pos} className="cursor-pointer">
                          {pos}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="experience" className="text-slate-700 font-medium">
                  Years of Experience *
                </Label>
                <Input
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  required
                  className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                  placeholder="e.g., 5 years in fiber splicing"
                  data-testid="career-input-experience"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-700 font-medium">
                  Tell Us About Yourself *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                  placeholder="Share your relevant experience and why you'd be a great fit..."
                  data-testid="career-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="career-submit-button"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    Submit Application
                    <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
